import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeftIcon, DocumentTextIcon, BuildingOfficeIcon, BriefcaseIcon } from '@heroicons/react/20/solid'
import { quotesApi, createCrudApi, type PaginatedResponse } from '../../lib/api'
import { QuoteForm } from '../../components/forms/QuoteForm'
import type { QuoteInput, Account, Opportunity } from '../../../shared/types'

const accountsApi = createCrudApi<Account, unknown>('accounts')
const opportunitiesApi = createCrudApi<Opportunity, unknown>('opportunities')

export function QuoteCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  // Pre-selected account/opportunity from URL params
  const preSelectedAccountId = searchParams.get('accountId')
  const preSelectedOpportunityId = searchParams.get('opportunityId')

  // Fetch pre-selected account details for context display
  const { data: preSelectedAccount, isLoading: accountLoading } = useQuery({
    queryKey: ['accounts', preSelectedAccountId],
    queryFn: () => accountsApi.get(preSelectedAccountId!),
    enabled: !!preSelectedAccountId,
  })

  // Fetch pre-selected opportunity details for context display
  const { data: preSelectedOpportunity, isLoading: opportunityLoading } = useQuery({
    queryKey: ['opportunities', preSelectedOpportunityId],
    queryFn: () => opportunitiesApi.get(preSelectedOpportunityId!),
    enabled: !!preSelectedOpportunityId,
  })

  const createMutation = useMutation({
    mutationFn: quotesApi.create,
    onSuccess: (newQuote) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      if (preSelectedAccountId) {
        queryClient.invalidateQueries({ queryKey: ['accounts', preSelectedAccountId] })
      }
      if (preSelectedOpportunityId) {
        queryClient.invalidateQueries({ queryKey: ['opportunities', preSelectedOpportunityId] })
      }
      navigate(`/quotes/${newQuote.id}`)
    },
  })

  const handleSubmit = (data: QuoteInput) => {
    // Ensure accountId is set from pre-selected if not in form
    if (preSelectedAccount && !data.accountId) {
      data.accountId = preSelectedAccount.id
    }
    // Ensure opportunityId is set from pre-selected if not in form
    if (preSelectedOpportunity && !data.opportunityId) {
      data.opportunityId = preSelectedOpportunity.id
    }
    createMutation.mutate(data)
  }

  const handleCancel = () => {
    if (preSelectedOpportunityId) {
      navigate(`/opportunities/${preSelectedOpportunityId}`)
    } else if (preSelectedAccountId) {
      navigate(`/accounts/${preSelectedAccountId}`)
    } else {
      navigate('/quotes')
    }
  }

  // Build default values from URL params
  const defaultValues: Partial<QuoteInput> = {
    accountId: preSelectedAccountId || undefined,
    opportunityId: preSelectedOpportunityId || undefined,
  }

  // If we have an opportunity, also set the account from it
  if (preSelectedOpportunity?.accountId && !preSelectedAccountId) {
    defaultValues.accountId = preSelectedOpportunity.accountId
  }

  return (
    <div>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to={preSelectedOpportunityId ? `/opportunities/${preSelectedOpportunityId}` : preSelectedAccountId ? `/accounts/${preSelectedAccountId}` : '/quotes'}
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            {preSelectedOpportunityId ? 'Back to Opportunity' : preSelectedAccountId ? 'Back to Account' : 'Back to Quotes'}
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-2 mb-2">
            <DocumentTextIcon className="h-5 w-5 text-brand-500" />
            <span className="text-lg font-bold text-gray-900">New Quote</span>
          </div>
          <p className="text-sm text-gray-500">
            Create a new quote for a customer
          </p>
        </div>

        {/* Pre-selected Account Context */}
        {preSelectedAccountId && (
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Account</h3>
            {accountLoading ? (
              <p className="text-sm text-gray-500">Loading account...</p>
            ) : preSelectedAccount ? (
              <div className="flex items-center gap-2">
                <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                <Link
                  to={`/accounts/${preSelectedAccount.id}`}
                  className="text-sm font-medium text-brand-600 hover:text-brand-800"
                >
                  {preSelectedAccount.name}
                </Link>
              </div>
            ) : (
              <p className="text-sm text-red-600">Account not found</p>
            )}
          </div>
        )}

        {/* Pre-selected Opportunity Context */}
        {preSelectedOpportunityId && (
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Opportunity</h3>
            {opportunityLoading ? (
              <p className="text-sm text-gray-500">Loading opportunity...</p>
            ) : preSelectedOpportunity ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                  <Link
                    to={`/opportunities/${preSelectedOpportunity.id}`}
                    className="text-sm font-medium text-brand-600 hover:text-brand-800"
                  >
                    {preSelectedOpportunity.name}
                  </Link>
                </div>
                {preSelectedOpportunity.amount && (
                  <div className="text-sm text-gray-500">
                    Amount: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(parseFloat(preSelectedOpportunity.amount))}
                  </div>
                )}
                <div className="text-sm text-gray-500">
                  Stage: {preSelectedOpportunity.stage.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
              </div>
            ) : (
              <p className="text-sm text-red-600">Opportunity not found</p>
            )}
          </div>
        )}

        {/* Help Text */}
        <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Tips</h3>
          <ul className="space-y-2 text-sm text-gray-500">
            <li className="flex items-start gap-2">
              <span className="text-brand-500">•</span>
              <span>Start by selecting an account to quote for</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-500">•</span>
              <span>Add line items after saving the quote</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-brand-500">•</span>
              <span>Valid until defaults to 30 days from today</span>
            </li>
          </ul>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] p-6">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <div className="mb-4">
              <Link
                to={preSelectedOpportunityId ? `/opportunities/${preSelectedOpportunityId}` : preSelectedAccountId ? `/accounts/${preSelectedAccountId}` : '/quotes'}
                className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                {preSelectedOpportunityId ? 'Back to Opportunity' : preSelectedAccountId ? 'Back to Account' : 'Back to Quotes'}
              </Link>
            </div>
            <div className="flex items-center gap-x-2 mb-4">
              <DocumentTextIcon className="h-5 w-5 text-brand-500" />
              <span className="font-bold text-gray-900">New Quote</span>
            </div>

            {/* Mobile account context */}
            {preSelectedAccount && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium">{preSelectedAccount.name}</span>
                </div>
              </div>
            )}

            {/* Mobile opportunity context */}
            {preSelectedOpportunity && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2">
                  <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium">{preSelectedOpportunity.name}</span>
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 hidden xl:block">
              Create Quote
            </h2>

            {createMutation.isError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : 'Failed to create quote. Please try again.'}
                </p>
              </div>
            )}

            <QuoteForm
              defaultValues={defaultValues}
              accountId={preSelectedAccountId || undefined}
              accountName={preSelectedAccount?.name}
              opportunityId={preSelectedOpportunityId || undefined}
              opportunityName={preSelectedOpportunity?.name}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={createMutation.isPending}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
